import { Activity, AlertTriangle } from "@tamagui/lucide-icons";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import {
  Sensor,
  SensorStatus,
  useSensorAlerts,
  useSensorEvents,
  useSensors,
} from "common";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router";
import {
  YStack,
  XStack,
  Text,
  Spinner,
  Button,
  Card,
  H2,
  H3,
  ScrollView,
  View,
  Separator,
} from "tamagui";
import { Table } from "tamagui-config";

const SensorEvents = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hubId, sensorId } = useParams<{ hubId: string; sensorId: string }>();
  const [sensor, setSensor] = useState<Sensor | null>(null);
  const [_sensorLoading, setSensorLoading] = useState<boolean>(true);
  const [_sensorError, setSensorError] = useState<Error | null>(null);
  const { getSensorById } = useSensors();

  const {
    sensorEvents: events,
    fetchSensorCurrentState,
    fetchSensorHistoricalState,
    loading: eventsLoading,
  } = useSensorEvents(undefined, sensorId);

  const {
    sensorAlerts,
    loading: alertsLoading,
    error: alertsError,
  } = useSensorAlerts(sensorId);

  const fetchSensor = async (sensorId: string) => {
    setSensorLoading(true);

    try {
      const sensor = await getSensorById(sensorId);
      if (sensor) {
        setSensor(sensor);
      } else {
        throw new Error("Sensor not found");
      }
    } catch (error) {
      setSensorError(
        error instanceof Error ? error : new Error("Failed to fetch sensor"),
      );
    } finally {
      setSensorLoading(false);
    }
  };

  const handleBackClick = useCallback(() => {
    navigate(`/base-stations/${hubId}/sensors`);
  }, [hubId, navigate]);

  const renderSensorDetails = () => {
    if (!sensor) return null;

    return (
      <Card padding="$4" marginBottom="$4">
        <XStack
          justifyContent="space-between"
          alignItems="center"
          marginBottom="$2"
        >
          <H2>{sensor.name}</H2>
          <View
            paddingHorizontal="$2"
            paddingVertical="$1"
            borderRadius="$4"
            backgroundColor={
              sensor.status === SensorStatus.Active ||
              sensor.status === SensorStatus.Active.toLowerCase()
                ? "$green8"
                : "$orange8"
            }
          >
            <Text color="white" fontWeight="bold">
              {sensor.status}
            </Text>
          </View>
        </XStack>
        <XStack flexWrap="wrap" gap="$4">
          <YStack space="$1" flex={1} minWidth={200}>
            <Text color="$gray11">{t("sensors.serialNumber")}</Text>
            <Text fontWeight="bold">{sensor.serialNumber}</Text>
          </YStack>
          <YStack space="$1" flex={1} minWidth={200}>
            <Text color="$gray11">{t("sensors.type")}</Text>
            <Text fontWeight="bold">{sensor.type}</Text>
          </YStack>
          <YStack space="$1" flex={1} minWidth={200}>
            <Text color="$gray11">{t("sensors.location")}</Text>
            <Text fontWeight="bold">
              {sensor.location || t("common.notSpecified")}
            </Text>
          </YStack>
          {sensor.lastReported && (
            <YStack space="$1" flex={1} minWidth={200}>
              <Text color="$gray11">{t("sensors.lastReported")}</Text>
              <Text fontWeight="bold">
                {formatDistanceToNow(new Date(sensor.lastReported * 1000), {
                  addSuffix: true,
                })}
              </Text>
            </YStack>
          )}
        </XStack>
      </Card>
    );
  };

  const formatEventValue = (value: string, type: string) => {
    switch (type.toLowerCase()) {
      case "temperature":
        return `${value}°C`;
      case "humidity":
        return `${value}%`;
      case "pressure":
        return `${value} hPa`;
      case "light":
        return `${value} lux`;
      default:
        return value;
    }
  };

  const renderSensorReadings = () => {
    const rowCounter = useRef(0);

    // Define columns for the events table
    const columnHelper = createColumnHelper<any>();
    const columns = [
      columnHelper.accessor("timestamp", {
        header: () => t("sensors.timestamp", "Timestamp"),
        cell: (info) => new Date(info.getValue() * 1000).toLocaleString(),
      }),
      columnHelper.accessor("eventType", {
        header: () => t("sensors.eventType", "Event Type"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("value", {
        header: () => t("sensors.value", "Value"),
        cell: (info) => formatEventValue(info.getValue(), sensor?.type || ""),
      }),
      columnHelper.accessor("metadata", {
        header: () => t("sensors.metadata", "Metadata"),
        cell: (info) => {
          const metadata = info.getValue();
          if (!metadata) return "-";
          if (typeof metadata === "string") {
            try {
              return JSON.stringify(JSON.parse(metadata), null, 2);
            } catch {
              return metadata;
            }
          }
          return JSON.stringify(metadata, null, 2);
        },
      }),
    ];

    const table = useReactTable({
      data: events || [],
      columns,
      getCoreRowModel: getCoreRowModel(),
    });

    const { getHeaderGroups, getRowModel } = table;
    const headerGroups = getHeaderGroups();
    const tableRows = getRowModel().rows;

    // Reset counter
    rowCounter.current = 0;

    const renderEventsTable = () => {
      if (!events || events.length === 0) {
        return (
          <YStack alignItems="center" padding="$4">
            <Activity size={32} color="$gray10" />
            <Text color="$gray10" marginTop="$2">
              {t("sensors.noReadingsYet", "No readings yet.")}
            </Text>
          </YStack>
        );
      }

      return (
        <Table
          alignCells={{ x: "flex-start", y: "center" }}
          alignHeaderCells={{ y: "center", x: "flex-start" }}
          cellHeight="$7"
          borderWidth={0}
          padding="$4"
          maxWidth="100%"
          gap="$5"
        >
          <Table.Head>
            {headerGroups.map((headerGroup) => {
              rowCounter.current++;
              return (
                <Table.Row key={headerGroup.id} justifyContent="flex-start">
                  {headerGroup.headers.map((header) => (
                    <Table.HeaderCell
                      key={header.id}
                      borderWidth={0}
                      justifyContent="flex-start"
                      width={header.column.id === "metadata" ? "50%" : "16.67%"}
                    >
                      <Text fontWeight="bold">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </Text>
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              );
            })}
          </Table.Head>
          <Table.Body>
            {tableRows.map((row) => {
              rowCounter.current++;
              return (
                <Table.Row key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell
                      key={cell.id}
                      borderWidth={0}
                      justifyContent="flex-start"
                      width={cell.column.id === "metadata" ? "50%" : "16.67%"}
                    >
                      <Text theme="alt1">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Text>
                    </Table.Cell>
                  ))}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      );
    };

    return (
      <YStack marginTop="$4">
        <H3 marginBottom="$2">
          {t("sensors.sensorReadings", "Sensor Readings")}
        </H3>
        <Card padding="$4">
          {eventsLoading ? (
            <YStack alignItems="center" padding="$4">
              <Spinner size="large" />
              <Text marginTop="$2">{t("common.loading", "Loading...")}</Text>
            </YStack>
          ) : (
            <YStack>
              <Text fontSize="$2" color="$gray11" marginBottom="$2">
                {t(
                  "sensors.realTimeUpdates",
                  "Real-time updates enabled. New readings will appear automatically.",
                )}
              </Text>
              <ScrollView>{renderEventsTable()}</ScrollView>
            </YStack>
          )}
        </Card>
      </YStack>
    );
  };

  const renderSensorAlerts = () => {
    const renderAlertsContent = () => {
      if (alertsLoading) {
        return (
          <YStack alignItems="center" padding="$4">
            <Spinner size="large" />
            <Text marginTop="$2">{t("common.loading", "Loading...")}</Text>
          </YStack>
        );
      }

      if (alertsError) {
        return (
          <YStack alignItems="center" padding="$4">
            <AlertTriangle size={32} color="$red10" />
            <Text color="$red10" marginTop="$2">
              {alertsError.message ||
                t("sensors.failedToLoadAlerts", "Failed to load alerts.")}
            </Text>
          </YStack>
        );
      }

      if (!sensorAlerts || sensorAlerts.length === 0) {
        return (
          <YStack alignItems="center" padding="$4">
            <AlertTriangle size={32} color="$gray10" />
            <Text color="$gray10" marginTop="$2">
              {t("sensors.noAlertsYet", "No alerts yet.")}
            </Text>
          </YStack>
        );
      }

      return (
        <YStack>
          <Text fontSize="$2" color="$gray11" marginBottom="$2">
            {t(
              "sensors.realTimeAlerts",
              "Real-time alerts enabled. New alerts will appear automatically.",
            )}
          </Text>
          <ScrollView>
            {sensorAlerts.map((alert) => (
              <Card
                key={alert.id + alert.timestamp}
                padding="$3"
                marginBottom="$3"
                bordered
              >
                <XStack justifyContent="space-between" alignItems="center">
                  <Text fontWeight="bold">
                    {alert.alertType} ({alert.severity})
                  </Text>
                  <Text color="$gray11" fontSize="$2">
                    {alert.timestamp
                      ? new Date(alert.timestamp * 1000).toLocaleString()
                      : "-"}
                  </Text>
                </XStack>
                <Separator marginVertical="$2" />
                <Text fontWeight="bold" marginBottom="$1">
                  {alert.message}
                </Text>
                {alert.value !== undefined && (
                  <Text color="$gray11" fontSize="$2" marginBottom="$1">
                    Value:{" "}
                    {typeof alert.value === "object"
                      ? JSON.stringify(alert.value, null, 2)
                      : String(alert.value)}
                  </Text>
                )}
                {alert.parsedMetadata &&
                  Object.keys(alert.parsedMetadata).length > 0 && (
                    <YStack marginTop="$1">
                      <Text color="$gray11" fontSize="$2" marginBottom="$1">
                        Metadata:
                      </Text>
                      <XStack flexWrap="wrap" gap="$2">
                        {Object.entries(alert.parsedMetadata).map(
                          ([key, value]) => (
                            <View
                              key={key}
                              paddingHorizontal="$2"
                              paddingVertical="$1"
                              borderRadius="$4"
                              backgroundColor="$blue5"
                            >
                              <Text color="$blue11" fontSize="$2">
                                {key}: {String(value)}
                              </Text>
                            </View>
                          ),
                        )}
                      </XStack>
                    </YStack>
                  )}
                <XStack marginTop="$2" gap="$4">
                  <Text color="$gray10" fontSize="$2">
                    Status: {alert.status}
                  </Text>
                  {alert.acknowledgedAt && (
                    <Text color="$gray10" fontSize="$2">
                      Ack:{" "}
                      {new Date(alert.acknowledgedAt * 1000).toLocaleString()}
                    </Text>
                  )}
                  {alert.acknowledgedBy && (
                    <Text color="$gray10" fontSize="$2">
                      By: {alert.acknowledgedBy}
                    </Text>
                  )}
                </XStack>
              </Card>
            ))}
          </ScrollView>
        </YStack>
      );
    };

    return (
      <YStack marginTop="$6">
        <H3 marginBottom="$2">{t("sensors.alerts", "Sensor Alerts")}</H3>
        <Card padding="$4">{renderAlertsContent()}</Card>
      </YStack>
    );
  };

  useEffect(() => {
    if (!sensorId) {
      return;
    }

    const startTime = Date.now() / 1000 - 7 * 86400;
    const endTime = Date.now() / 1000;
    const rollup = 86400;

    fetchSensor(sensorId);
    fetchSensorCurrentState(sensorId);
    fetchSensorHistoricalState(sensorId, startTime, endTime, rollup);
  }, [sensorId]);

  return (
    <YStack flex={1} padding="$4" space>
      <XStack alignItems="center" space="$2" marginBottom="$4">
        <Button
          size="$3"
          chromeless
          onPress={handleBackClick}
          icon={<Text fontSize="$6">←</Text>}
        />
        <Text fontSize="$8" fontWeight="bold">
          {t("sensors.sensorReadings")}
        </Text>
      </XStack>
      {renderSensorDetails()}
      {renderSensorReadings()}
      {renderSensorAlerts()}
    </YStack>
  );
};

export default SensorEvents;
